/**
 * Bailey — admin AI chat tab.
 *
 * Streams Claude responses with markdown rendering and inline tool-call
 * cards. Founders only (gated server-side).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AdminWrapper from './AdminWrapper';
import { baileyService } from '../../_services/bailey.service';
import './Admin.css';
import './Bailey.css';

// Replies can quote records (salon names, notes) that anyone could have
// written, so a picture in one is never loaded: it would fetch its address
// with no click.
const MD_COMPONENTS = { img: ({ alt }) => (alt ? <span>{alt}</span> : null) }; // eslint-disable-line react/prop-types

const SUGGESTED_PROMPTS = [
  'Top 5 salons by completed bookings this month',
  'How many active SE hairdressers do we have?',
  'Show MTD payment processing volume by channel',
  'Which salons signed up in the last 30 days?'
];

// ---------- helpers ----------
function formatRelativeTime(date) {
  if (!date) return '';
  const d = new Date(date);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
}

// ---------- subcomponents ----------

function ToolCard({ name, ok, content, input }) {
  const [open, setOpen] = useState(false);
  const status = ok === undefined ? 'running' : ok ? 'done' : 'failed';
  const icon = status === 'running' ? '⏳' : ok ? '✓' : '⚠';
  const summary = (() => {
    try {
      const parsed = JSON.parse(content || '');
      if (Array.isArray(parsed)) return `${parsed.length} result${parsed.length === 1 ? '' : 's'}`;
      if (parsed && typeof parsed === 'object' && 'count' in parsed) return `count: ${parsed.count}`;
      return null;
    } catch (err) {
      return null;
    }
  })();

  return (
    <div className="bailey-tool">
      <div className="bailey-tool-header" onClick={() => setOpen((v) => !v)}>
        <span className="bailey-tool-icon">{icon}</span>
        <span className="bailey-tool-name">{name}</span>
        <span className={`bailey-tool-status${status === 'failed' ? ' error' : ''}`}>
          {status === 'running' ? 'querying…' : summary || (ok ? 'done' : 'failed')}
          {' · '}
          {open ? 'hide' : 'show'}
        </span>
      </div>
      {open && (
        <div className="bailey-tool-body">
          {input ? <div style={{ marginBottom: 8, color: '#666' }}>{JSON.stringify(input, null, 2)}</div> : null}
          {content || ''}
        </div>
      )}
    </div>
  );
}

function Message({ msg, isStreaming }) {
  const isUser = msg.role === 'user';
  return (
    <div className="bailey-msg">
      <div className={`bailey-msg-avatar ${isUser ? 'user' : 'assistant'}`}>
        {isUser ? 'You' : '🦦'}
      </div>
      <div className="bailey-msg-body">
        {msg.parts && msg.parts.length > 0
          ? msg.parts.map((part, i) => {
              if (part.type === 'text') {
                return (
                  <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                    {part.text}
                  </ReactMarkdown>
                );
              }
              if (part.type === 'tool') {
                return <ToolCard key={i} {...part} />;
              }
              return null;
            })
          : (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                {msg.content || ''}
              </ReactMarkdown>
            )}
        {isStreaming && <span className="bailey-cursor" />}
      </div>
    </div>
  );
}

// ---------- main ----------

const MODEL_PREF_KEY = 'bailey.model';

function BaileyPage(props) {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{ role, parts: [{type, ...}], content }]
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(
    () => (typeof localStorage !== 'undefined' && localStorage.getItem(MODEL_PREF_KEY)) || ''
  );
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);

  // Auto-scroll on new content
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // Load session list + model list on mount
  useEffect(() => {
    let mounted = true;
    baileyService
      .listSessions()
      .then((rows) => mounted && setSessions(rows))
      .catch(() => {});
    baileyService
      .listModels()
      .then((rows) => {
        if (!mounted) return;
        setAvailableModels(rows);
        // If no preference saved, fall back to the first model returned (server's default)
        if (!selectedModel && rows.length > 0) setSelectedModel(rows[0].id);
      })
      .catch(() => {});
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist model preference
  useEffect(() => {
    if (selectedModel) localStorage.setItem(MODEL_PREF_KEY, selectedModel);
  }, [selectedModel]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
  }, [input]);

  const refreshSessions = useCallback(() => {
    baileyService.listSessions().then(setSessions).catch(() => {});
  }, []);

  const newChat = useCallback(() => {
    if (streaming && abortRef.current) abortRef.current.abort();
    setActiveSessionId(null);
    setMessages([]);
    setError(null);
    setInput('');
  }, [streaming]);

  const openSession = useCallback(async (sessionId) => {
    if (streaming && abortRef.current) abortRef.current.abort();
    setActiveSessionId(sessionId);
    setError(null);
    setStreaming(false);
    try {
      const turns = await baileyService.getSessionTranscript(sessionId);
      setMessages(turns.map((t) => ({ role: t.role, content: t.content, parts: [] })));
    } catch (err) {
      setError(err.message);
    }
  }, [streaming]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setError(null);
    setInput('');

    const userMsg = { role: 'user', content: text, parts: [] };
    const assistantMsg = { role: 'assistant', content: '', parts: [{ type: 'text', text: '' }] };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      let resolvedSessionId = activeSessionId;
      await baileyService.streamChat(
        { sessionId: activeSessionId || undefined, message: text, model: selectedModel || undefined },
        (event) => {
          setMessages((prev) => {
            const next = [...prev];
            const last = { ...next[next.length - 1] };
            const parts = [...(last.parts || [])];

            if (event.type === 'session') {
              if (!resolvedSessionId) resolvedSessionId = event.sessionId;
              return next; // no message change
            }
            if (event.type === 'text_delta') {
              // Append to the trailing text part, or create one
              const lastPart = parts[parts.length - 1];
              if (lastPart && lastPart.type === 'text') {
                parts[parts.length - 1] = { ...lastPart, text: lastPart.text + event.text };
              } else {
                parts.push({ type: 'text', text: event.text });
              }
            } else if (event.type === 'tool_call') {
              parts.push({
                type: 'tool',
                id: event.id,
                name: event.name,
                input: event.input,
                ok: undefined,
                content: ''
              });
            } else if (event.type === 'tool_result') {
              const idx = parts.findIndex((p) => p.type === 'tool' && p.id === event.id);
              if (idx >= 0) {
                parts[idx] = { ...parts[idx], ok: event.ok, content: event.content };
              }
            } else if (event.type === 'error') {
              parts.push({ type: 'text', text: `\n\n_Error: ${event.message}_` });
            }

            last.parts = parts;
            // Reflect concatenated text into .content for transcript hydration
            last.content = parts.filter((p) => p.type === 'text').map((p) => p.text).join('');
            next[next.length - 1] = last;
            return next;
          });
        },
        controller.signal
      );

      if (resolvedSessionId && resolvedSessionId !== activeSessionId) {
        setActiveSessionId(resolvedSessionId);
      }
      refreshSessions();
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Stream failed');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, streaming, activeSessionId, refreshSessions]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const applySuggested = (text) => {
    setInput(text);
    setTimeout(() => textareaRef.current && textareaRef.current.focus(), 0);
  };

  const isEmpty = messages.length === 0 && !streaming;

  return (
    <AdminWrapper {...props}>
      <div className="bailey-page">
        {/* Sessions */}
        <aside className="bailey-sessions">
          <div className="bailey-sessions-header">
            <button className="bailey-new-chat-btn" onClick={newChat}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New chat
            </button>
          </div>
          <div className="bailey-sessions-list">
            {sessions.length === 0 ? (
              <div className="bailey-sessions-empty">No chats yet</div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.sessionId}
                  className={`bailey-session-row${s.sessionId === activeSessionId ? ' active' : ''}`}
                  onClick={() => openSession(s.sessionId)}
                >
                  <div className="bailey-session-row-title">
                    {s.firstUserMessage || 'Untitled'}
                  </div>
                  <div className="bailey-session-row-meta">
                    {formatRelativeTime(s.lastMessageAt)}
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Chat */}
        <section className="bailey-chat">
          <div className="bailey-chat-header">
            <span style={{ fontSize: 18 }}>🦦</span>
            <div>
              <div className="bailey-chat-header-title">Bailey</div>
              <div className="bailey-chat-header-sub">Read-only DB access</div>
            </div>
            {availableModels.length > 0 && (
              <div className="bailey-model-picker">
                <label className="bailey-model-label">Model</label>
                <select
                  className="bailey-model-select"
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  disabled={streaming}
                  title={availableModels.find((m) => m.id === selectedModel)?.hint || ''}
                >
                  {availableModels.map((m) => (
                    <option key={m.id} value={m.id} title={m.hint}>{m.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isEmpty ? (
            <div className="bailey-empty">
              <div className="bailey-empty-inner">
                <div className="bailey-empty-emoji">🦦</div>
                <div className="bailey-empty-title">Hey, I'm Bailey.</div>
                <div className="bailey-empty-sub">
                  Ask me anything about Beu's data — bookings, salons, hairdressers, payouts, finance. I read; I don't change anything.
                </div>
                <div className="bailey-suggested">
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button key={i} className="bailey-suggested-card" onClick={() => applySuggested(p)}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bailey-messages" ref={messagesEndRef}>
              {messages.map((m, i) => (
                <Message
                  key={i}
                  msg={m}
                  isStreaming={streaming && i === messages.length - 1 && m.role === 'assistant'}
                />
              ))}
            </div>
          )}

          <div className="bailey-composer">
            <div className="bailey-composer-inner">
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder="Ask Bailey…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={streaming}
              />
              <button
                className="bailey-send"
                onClick={send}
                disabled={!input.trim() || streaming}
                title="Send (Enter)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            </div>
            {error && <div className="bailey-composer-error">{error}</div>}
            <div className="bailey-composer-hint">
              Bailey only reads; nothing here changes data. Press Enter to send · Shift+Enter for newline.
            </div>
          </div>
        </section>
      </div>
    </AdminWrapper>
  );
}

export default BaileyPage;
